<template>
  <SogForm
    ref="_form"
    :validations="_validations"
  >
    <div v-show="_showFullName">
      <SogFormField>
        <SogTextBox
          v-model="_value.address.fullName"
          placeholder="Abweichende Name"
          autocomplete="true"
          name="FullName"
          width="220px"
        />
      </SogFormField>
      <SogFormField>
        <SogTextBox
          v-model="_value.address.line1"
          placeholder="Zusatz"
          autocomplete="true"
          name="Line1"
          width="220px"
        />
      </SogFormField>
    </div>
    <SogFormField>
      <SogTextBox
        v-model="_value.address.street"
        placeholder="Strasse"
        autocomplete="true"
        name="Street"
        width="220px"
      />
    </SogFormField>
    <SogFormField>
      <SogTextBox
        v-model="_value.fullHouseNumber"
        placeholder="Hausnummer"
        autocomplete="true"
        name="HouseNumber"
        width="220px"
      />
    </SogFormField>
    <SogFormField>
      <SogTextBox
        v-model="_value.address.postalCode"
        placeholder="Postleitzahl"
        autocomplete="true"
        name="PostalCode"
        max-length="8"
        width="220px"
      />
    </SogFormField>
    <SogFormField>
      <SogTextBox
        v-model="_value.address.city"
        placeholder="Stadt"
        autocomplete="true"
        name="City"
        width="220px"
      />
    </SogFormField>
    <SogFormField>
      <SogDropDownList
        v-model="_countryCode"
        placeholder="Land"
        :data-source="_countries"
        :fields="_fieldsCountries"
        name="Country"
        width="220px"
      />
    </SogFormField>
  </SogForm>
</template>



<script setup lang="ts">
import SogFormField from "@/shared/ui/SogFormField.vue";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import SogDropDownList from "@/shared/ui/SogDropDownList.vue";
import SogForm from "@/shared/ui/SogForm.vue";
import Address from "@/shared/model/Address";
import {computed, onBeforeMount, reactive, ref, watch} from "vue";
import {countries} from "@/shared/model/Country";
import SogFormValidationRule from "@/shared/ui/types/SogFormValidationRule";
import PersonAddressEditComponent from "@/modules/OrganisationStructure/components/PersonAddressEditComponent.vue";

interface Props {
  address:Address
  showFullName?:boolean
}

const props = withDefaults(defineProps<Props>(), {
      showFullName: true,
      address: () => {
        const t = new Address();
        t.countryCode = "DE"
        return t;
      }
    }
)

const _form = ref<InstanceType<typeof SogForm>  | null>(null)

const _fullHouseNumber = ref("")
const _value = reactive({address:props.address,fullHouseNumber:_fullHouseNumber})
const _countryCode = ref(props.address.countryCode)

const _countries = countries;
const _fieldsCountries =  { text: 'countryName', value: 'countryCode' }

const _validations:SogFormValidationRule[]=[];

const _showFullName = computed(()=>{
  return props.showFullName && props.showFullName===true
})

watch( props,  () => {
      _value.address = props.address;

      _countryCode.value = _value.address.countryCode ?? ""
      _fullHouseNumber.value =
        (_value.address.houseNumber != null ? _value.address.houseNumber.toString() : "")
        + (_value.address.houseNumberAddition ?? "")
      // deep: Props sind shallowReactive – ohne deep feuert der Watch nur bei
      // Referenzwechsel, nicht wenn address in-place mutiert wird.
    },{immediate:true, deep:true}

)

watch((_fullHouseNumber),(value)=>{
  const a = value.match(/^\d+/g)
  if(a &&a.length>0){
    _value.address.houseNumber = parseInt(a[0])
    _value.address.houseNumberAddition = value.substring(a[0].length)
  }
  else {
    // houseNumber mitleeren, sonst stellt der deep-Watch den alten Wert im Feld wieder her
    _value.address.houseNumber = undefined
    _value.address.houseNumberAddition = value
  }

})


watch((_countryCode),(value)=>{
  _form.value?.removeRules("PostalCode")

  if(_form.value&&value==="DE"){
    _form.value?.addRules(  {name:'PostalCode',rules:{required:[true,"Die Postleitzahl muss aus genau fünf Ziffern bestehen"], minLength:[5,"Die Postleitzahl muss aus genau fünf Ziffern bestehen"],maxLength:[5,"die Postleitzahl muss aus genau fünf Ziffern bestehen"]}} )
  }
  else {
    _form.value?.addRules(  {name:'PostalCode',rules:{maxLength:[8,"Die Postleitzahl muss maximal aus 8 Ziffern bestehen"]}} )
  }
  _value.address.countryCode = value
}, {immediate:true})


onBeforeMount(()=>{

  _validations.push({name:'Street',rules:{required:[true,"Geben Sie mindestens eine Adresszeile ein."]}})
  if (props.address.countryCode === "DE") {
    _validations.push({
      name: 'PostalCode',
      rules: {
        required: [true, "Die Postleitzahl muss aus genau fünf Ziffern bestehen"],
        minLength: [5, "Die Postleitzahl muss aus genau fünf Ziffern bestehen"],
        maxLength: [5, "die Postleitzahl muss aus genau fünf Ziffern bestehen"]
      }
    })
  }
  else {
    _validations.push({
      name: 'PostalCode',
      rules: { maxLength: [8, "Die Postleitzahl muss maximal aus 8 Ziffern bestehen"] }
    })

  }
})
const validate = ( ):boolean => {
  if(_form.value)
    return _form.value.validate();
  return false;
}
const getAddress = ():Address => {
  return _value.address;
}

defineExpose({
  validate,getAddress
})
</script>

<style scoped>

</style>